
import React, { FC, useEffect, useRef, useState, useCallback } from "react";
import styles from "./GuestsField.module.scss";
import Guests, { Room } from "@/components/forms/selects/Guests/GuestsSelector";
import { useTranslations } from "next-intl";
import { GuestIcon } from "@/components/icons";
import { createPortal } from "react-dom";
import { useRouter } from "next/router";

interface GuestsFieldProps {
  onGuestChange: (rooms: Room[]) => void;
  guestRooms: Room[];
  isBannerBox?: boolean;
  className?: string;
}

const GuestsField: FC<GuestsFieldProps> = ({
  onGuestChange,
  guestRooms,
  isBannerBox,
  className,
}) => {
  const t = useTranslations("guests");
  const { locale } = useRouter();

  const [rooms, setRooms] = useState<Room[]>(guestRooms);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const dropdownRef = useRef<HTMLDivElement>(null);
  const guestMenuRef = useRef<HTMLDivElement>(null);

  const adultsTotal = rooms.reduce((total, room) => total + room.adults, 0);
  const childrenTotal = rooms.reduce(
    (total, room) => total + room.children.length,
    0
  );

  const handleApply = (updatedrooms: Room[]) => {
    setRooms(updatedrooms);
    onGuestChange(updatedrooms);
  };

  const formatSummary = (
    t: (key: string, values?: Record<string, any>) => string
  ) => {
    let summary = `${t("guestRoom", { count: rooms.length })}, `;
    summary += `${t("adult", { count: adultsTotal })}${childrenTotal > 0 ? ", " : ""}`;
    summary += childrenTotal ? `${t("child", { count: childrenTotal })}` : "";
    return summary.length > 55 ? `${summary.slice(0, 55)}...` : summary;
  };

  const updateDropdownPosition = useCallback(() => {
    if (!guestMenuRef.current) return;

    const rect = guestMenuRef.current.getBoundingClientRect();
    const isRTL = locale === "ar";

    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 8,
      zIndex: 9999,
      width: rect.width,

      ...(isRTL
        ? { right: window.innerWidth - rect.right }
        : { left: rect.left }),
    });
  }, [locale]);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handle = () => updateDropdownPosition();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);

    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        guestMenuRef.current &&
        !guestMenuRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div
      className={`${styles.SearchField} h-[60px] ${
        isBannerBox ? "!border-[#E2EDF8]" : ""
      } ${className} relative`}
      data-testid="CustomSearchField"
      onClick={(e) => {
        e.stopPropagation();
        setIsOpen((prev) => !prev);
      }}
      ref={guestMenuRef}
      role="button"
      tabIndex={0}
      aria-label="Toggle guest selection menu"
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setIsOpen((prev) => !prev);
        }
      }}
    >
      <span className={styles.icon}>
        <GuestIcon className="text-primary" />
      </span>

      <div className={styles.content}>
        <div className={`${styles.guestContainer}`} data-testid="GuestsField">
          <span className={`${styles.guestNumber} max-c-md:!text-sm max-c-md:!leading-5`}>
            {formatSummary(t)}
          </span>

          {isOpen &&
            createPortal(
              <div
                ref={dropdownRef}
                style={dropdownStyle}
                className={`${styles.dropDown} relative`}
                role="dialog"
                tabIndex={-1}
                aria-label="Adjust guest rooms"
                onClick={(e) => e.stopPropagation()}
              >
            
               <div className="h-3 w-3 border-t border-l border-gray-200 absolute top-[1px] ltr:left-[15px] rtl:right-[20px] bg-white rotate-45"></div>

                <Guests
                  onApply={handleApply}
                  rooms={rooms}
                  onClose={() => setIsOpen(false)}
                />
              </div>,
              document.body
            )}
        </div>
      </div>
    </div>
  );
};

export default GuestsField;
